import React, { FC, useCallback, useEffect, useState } from "react";

import Popover from "antd/es/popover";
import { endOfDay, endOfYesterday, isAfter, isBefore, startOfDay, subYears } from "date-fns/esm";
import { useIntl } from "react-intl";

import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import RangePicker from "@app/component/input/RangePicker";

import { messages } from ".";
import styles from "./LocationFilters.module.scss";

export const isDateDisabled = (date: Date) =>
  isAfter(date, endOfYesterday()) || isBefore(date, subYears(Date.now(), 1));

export interface CustomRangePickerProps {
  dateRange: DateRange;
  onChange: (dateRange: DateRange) => void;
}

const CustomRangePicker: FC<CustomRangePickerProps> = ({ dateRange, onChange }) => {
  const intl = useIntl();
  const [open, show, hide] = useBoolean();
  const [newDateRange, setNewDateRange] = useState<DateRange>(dateRange);

  const handleDateRangeChange = useCallback(
    ([startDate, endDate]) => {
      if (startDate && endDate) {
        setNewDateRange({ endDate: endOfDay(endDate), startDate: startOfDay(startDate) });
      }
    },
    [setNewDateRange]
  );

  const handleCancel = useCallback(() => {
    setNewDateRange(dateRange);
    hide();
  }, [dateRange, hide]);

  const handleApply = useCallback(() => {
    onChange(newDateRange);
    hide();
  }, [newDateRange, hide, onChange]);

  useEffect(() => setNewDateRange(dateRange), [dateRange]);

  return (
    <Popover
      content={
        <div className={styles.popover}>
          <span className={styles.title}>{intl.formatMessage(messages.changeDateRange)}</span>
          <div>
            <div>{intl.formatMessage(messages.selectRange)}</div>
            <RangePicker
              className={styles.datePicker}
              disabledDate={isDateDisabled}
              onChange={handleDateRangeChange}
              value={[newDateRange?.startDate ?? null, newDateRange?.endDate ?? null]}
            />
          </div>
          <div className={styles.buttons}>
            <Button onClick={handleCancel}>{intl.formatMessage(messages.cancel)}</Button>
            <Button onClick={handleApply} type="primary">
              {intl.formatMessage(messages.apply)}
            </Button>
          </div>
        </div>
      }
      open={open}
      placement="bottom"
      trigger={["click"]}
    >
      <Button onClick={show} size="small" type="link">
        {intl.formatMessage(messages.change)}
      </Button>
    </Popover>
  );
};

export default CustomRangePicker;
